import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const WishlistContext = createContext(null);

const STORAGE_KEY = 'shopimax_wishlist_v1';

export const WishlistProvider = ({ children }) => {
  const [items, setItems] = useState([]);

  // Hydrate from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setItems(parsed);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Persist to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      // ignore quota errors
    }
  }, [items]);

  const isInWishlist = useCallback(
    (productId) => items.some((it) => it.id === productId),
    [items]
  );

  const addToWishlist = useCallback((product) => {
    if (!product || !product.id) return;
    setItems((prev) => {
      if (prev.some((p) => p.id === product.id)) return prev;
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          price: product.price,
          image_url: product.image_url,
          vendor_id: product.vendor_id,
          vendor_name: product.vendor_name,
          added_at: new Date().toISOString(),
          saved_price: product.price ?? null,
          saved_image_url: product.image_url || null,
        },
      ];
    });
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    setItems((prev) => prev.filter((p) => p.id !== productId));
  }, []);

  const toggleWishlist = useCallback(
    (product) => {
      if (!product || !product.id) return;
      setItems((prev) => {
        if (prev.some((p) => p.id === product.id)) {
          return prev.filter((p) => p.id !== product.id);
        }
        return [
          ...prev,
          {
            id: product.id,
            name: product.name,
            price: product.price,
            image_url: product.image_url,
            vendor_id: product.vendor_id,
            vendor_name: product.vendor_name,
            added_at: new Date().toISOString(),
          saved_price: product.price ?? null,
          saved_image_url: product.image_url || null,
          },
        ];
      });
    },
    []
  );

  const clearWishlist = useCallback(() => setItems([]), []);

  return (
    <WishlistContext.Provider
      value={{
        items,
        count: items.length,
        isInWishlist,
        addToWishlist,
        removeFromWishlist,
        toggleWishlist,
        clearWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) {
    // Safe fallback so consumers don't crash if provider is missing
    return {
      items: [],
      count: 0,
      isInWishlist: () => false,
      addToWishlist: () => {},
      removeFromWishlist: () => {},
      toggleWishlist: () => {},
      clearWishlist: () => {},
    };
  }
  return ctx;
};

export default WishlistContext;
